import {useCallback} from "react";
import {useQueryClient} from "@tanstack/react-query";
import CONFIG from "@commons/Config";
import UserService from "@lib/User/UserService";
import CmsService from "@lib/Cms/CmsService";
import {qk} from "@queries/keys";
import {BootFail, useAppContextStore} from "@store/AppContextStore";
import {User} from "@lib/User/UserModels";
import {getCountryFromUser} from "@commons/Utils";
import ProjectService from "@lib/Project/ProjectService";
import {WebappCms} from "@lib/Cms/CmsModels";
import {mergeGlobalWithModules} from "@lib/Cms/CmsUtils";
import {DEFAULT_LANG} from "@i18n/language";

function failFromUserError(e: any): BootFail {
    const status = e?.status ?? e?.response?.status;
    if (status === 404) return "ME_USER_MISSING";
    return "UNKNOWN";
}

let bootPromise: Promise<any> | null = null;

export function usePrivateBootstrap() {

    const qc = useQueryClient();
    const setCtx = useAppContextStore((s) => s.setCtx);

    const run = useCallback(
        async (params: { token: string | null; b2cCountry: string | null | undefined }) => {

            const s0 = useAppContextStore.getState();

            if (s0.status === "ready") return {ok: true as const};

            if (bootPromise) return bootPromise;

            bootPromise = (async () => {
                try {

                    const token = params.token;
                    const b2cCountry = params.b2cCountry?.toUpperCase() ?? null;
                    
                    console.log("B2C country", b2cCountry)
                    
                    if (!token) {
                        console.log("Token not provided, not authenticated.")
                        setCtx({status: "error", statusReason: "NOT_AUTHENTICATED"});
                        return {ok: false as const, reason: "NOT_AUTHENTICATED" as const};
                    }

                    if (!b2cCountry) {
                        console.log("B2C country not provided, not authenticated.")
                        setCtx({status: "error", statusReason: "MISSING_B2C_COUNTRY"});
                        return {ok: false as const, reason: "MISSING_B2C_COUNTRY" as const};
                    }

                    if (!CONFIG.enabledCountries.includes(b2cCountry)) {
                        console.log("Country not enabled", b2cCountry, CONFIG.enabledCountries)
                        setCtx({status: "error", statusReason: "COUNTRY_DISABLED", b2cCountry});
                        return {ok: false as const, reason: "COUNTRY_DISABLED" as const};
                    }

                    setCtx({status: "loading", statusReason: null, b2cCountry});

                    let tenantId: string | null = null;
                    let projectId: string | null = null;

                    try {
                        const projectSettings = await qc.fetchQuery({
                            queryKey: qk.projectSettings(b2cCountry),
                            queryFn: () => ProjectService.getByCountry(b2cCountry),
                            staleTime: 10 * 60_000,
                            retry: 1,
                        });

                        tenantId = projectSettings.tenantId ?? null;
                        projectId = projectSettings.projectId ?? null;

                        setCtx({
                            tenantId,
                            projectId,
                            language: projectSettings.language?.toLowerCase() ?? DEFAULT_LANG,
                            projectTimezone: projectSettings.timezone ?? Intl.DateTimeFormat().resolvedOptions().timeZone,
                            catalogInitiativeId: projectSettings.catalogId ?? "",
                            currencyName: projectSettings.currency ?? "",
                            currencySymbol: projectSettings.currencySymbol ?? "",
                        });

                    } catch {
                        setCtx({status: "error", statusReason: "TENANT_FAILED", b2cCountry});
                        return {ok: false as const, reason: "TENANT_FAILED" as const};
                    }

                    let user: User;
                    try {
                        user = await qc.fetchQuery({
                            queryKey: qk.jnextUser(),
                            queryFn: () => UserService.getUser(token),
                            staleTime: 60_000,
                            retry: false,
                        });
                    } catch (e: any) {
                        const reason = failFromUserError(e);
                        setCtx({status: "error", statusReason: reason, b2cCountry});
                        return {ok: false as const, reason};
                    }

                    const jnextCountry = getCountryFromUser(user);
                    if (!jnextCountry || jnextCountry !== b2cCountry) {
                        setCtx({status: "error", statusReason: "COUNTRY_MISMATCH", b2cCountry, meCountry: jnextCountry});
                        return {ok: false as const, reason: "COUNTRY_MISMATCH" as const};
                    }

                    /**
                     * Global and module cms
                     */
                    let cms: WebappCms;

                    try {
                        const [globalCms, userCms, eventCms, rewardCms] = await Promise.all([
                            CmsService.getWebapp(),
                            CmsService.getUserCms(),
                            CmsService.getEventCms(),
                            CmsService.getRewardCms(),
                        ]);

                        cms = mergeGlobalWithModules(globalCms, userCms.configuration, eventCms.configuration, rewardCms.configuration);

                    } catch {
                        setCtx({status: "error", statusReason: "CMS_FAILED", b2cCountry, meCountry: jnextCountry, tenantId});
                        return {ok: false as const, reason: "CMS_FAILED" as const};
                    }

                    setCtx({
                        status: "ready",
                        statusReason: null,
                        b2cCountry,
                        meCountry: jnextCountry,
                        tenantId,
                        projectId,
                        cms: cms,
                    });

                    return {ok: true as const};

                } finally {
                    const s1 = useAppContextStore.getState();
                    if (s1.status !== "ready") bootPromise = null;
                }
            })();

            return bootPromise;
        },
        [qc, setCtx]
    );

    return {run};
}
